import React, { useState, useEffect, useCallback } from 'react';
import { fetchWebhookEvents, fetchWebhookEventDetail, retryWebhookEvent } from '../../api/webhookApi';
import { toast } from 'react-toastify';
import '../../styles/webhooks.css';

const TABS = [
  { id: 'events', label: 'Events', icon: 'bi-list-ul' },
  { id: 'stats',  label: 'Stats',  icon: 'bi-bar-chart-line' },
];

const STATUS_FILTERS = [
  { id: '',           label: 'All' },
  { id: 'pending',    label: 'Pending' },
  { id: 'processing', label: 'Processing' },
  { id: 'done',       label: 'Done' },
  { id: 'failed',     label: 'Failed' },
];

const STATUS_CONFIG = {
  pending:    { className: 'status-pending',    icon: 'bi-hourglass-split', label: 'Pending' },
  processing: { className: 'status-running',    icon: 'bi-arrow-repeat',    label: 'Processing' },
  done:       { className: 'status-completed',  icon: 'bi-check-circle-fill', label: 'Done' },
  failed:     { className: 'status-failed',     icon: 'bi-x-circle-fill',   label: 'Failed' },
};

const formatTime = (timestamp) => {
  if (!timestamp) return '--';
  const date = new Date(timestamp);
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const WebhooksPage = () => {
  const [activeTab, setActiveTab] = useState('events');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [configured, setConfigured] = useState(true);
  const [hmacConfigured, setHmacConfigured] = useState(true);
  const [detailEvent, setDetailEvent] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const data = await fetchWebhookEvents({ limit: 100, status: statusFilter || undefined });
      setEvents(data.events || []);
      setStats(data.stats || null);
      setTotal(data.total || 0);
      setConfigured(data.configured !== false);
      setHmacConfigured(typeof data.hmacConfigured === 'boolean' ? data.hmacConfigured : true);
    } catch {
      toast.error('Failed to load webhook events');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => loadData(true);

  const handleRetry = async (id) => {
    setRetryingId(id);
    try {
      await retryWebhookEvent(id);
      toast.success('Event re-queued');
      await loadData(true);
    } catch {
      toast.error('Failed to retry event');
    } finally {
      setRetryingId(null);
    }
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetailEvent({ id });
    try {
      const data = await fetchWebhookEventDetail(id);
      setDetailEvent(data.event);
    } catch {
      toast.error('Failed to load event detail');
      setDetailEvent(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setDetailEvent(null);

  const getStatusConfig = (status) =>
    STATUS_CONFIG[status] || { className: 'status-unknown', icon: 'bi-question-circle', label: status };

  const renderWebhookBanners = () => (
    <>
      {!configured && (
        <div className="webhook-warning-banner">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div>
            <strong>Webhooks not configured</strong>
            <span>
              Set both <code>TESTPRESS_WEBHOOK_PRIVATE_KEY</code> and <code>TESTPRESS_WEBHOOK_PUBLIC_KEY</code> (environment variables or
              Settings → Integrations → Webhook Keys) so incoming Testpress webhooks can be verified. For local testing only, you may set{' '}
              <code>TESTPRESS_WEBHOOK_SKIP_HMAC=true</code> to accept requests without HMAC verification (do not use in production).
            </span>
          </div>
        </div>
      )}
      {configured && !hmacConfigured && (
        <div className="webhook-insecure-banner">
          <i className="bi bi-shield-exclamation"></i>
          <div>
            <strong>HMAC verification disabled</strong>
            <span>
              <code>TESTPRESS_WEBHOOK_SKIP_HMAC</code> is enabled — incoming webhooks are accepted without signature verification. Configure
              Private Key and Public Key before production.
            </span>
          </div>
        </div>
      )}
    </>
  );

  const renderLoading = () => (
    <div className="webhook-loading">
      <div className="webhook-loading-spinner"></div>
      <span>Loading webhook events...</span>
    </div>
  );

  const renderEmptyState = () => (
    <div className="webhook-empty">
      <i className="bi bi-inbox webhook-empty-icon"></i>
      <p>No events yet</p>
      <span>Webhook events from Testpress will appear here once received.</span>
    </div>
  );

  const renderStatusFilters = () => (
    <div className="webhook-filter-row">
      {STATUS_FILTERS.map((f) => (
        <button
          key={f.id || 'all'}
          className={`webhook-filter-chip ${statusFilter === f.id ? 'active' : ''}`}
          onClick={() => setStatusFilter(f.id)}
        >
          {f.label}
          {stats?.byStatus && f.id && stats.byStatus[f.id] != null && (
            <span className="webhook-filter-count">{stats.byStatus[f.id]}</span>
          )}
        </button>
      ))}
    </div>
  );

  const renderEventsTab = () => {
    if (loading) return renderLoading();

    return (
      <div className="webhook-events-card">
        <div className="webhook-events-toolbar">
          <h2 className="webhook-events-title">
            <i className="bi bi-list-ul"></i>
            Recent Events
            <span className="webhook-events-count">{total} total</span>
          </h2>
        </div>

        {renderStatusFilters()}

        {events.length === 0 ? renderEmptyState() : (
          <div className="webhook-events-table-wrap">
            <table className="webhook-events-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Status</th>
                  <th>Attempt ID</th>
                  <th>User ID</th>
                  <th>Type</th>
                  <th>Attempts</th>
                  <th>Last error</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => {
                  const cfg = getStatusConfig(event.status);
                  return (
                    <tr key={event.id}>
                      <td className="webhook-cell-time">{formatTime(event.received_at)}</td>
                      <td>
                        <span className={`history-cell-status-badge ${cfg.className}`}>
                          <i className={`bi ${cfg.icon}`}></i>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="webhook-cell-mono">{event.attempt_id ?? '--'}</td>
                      <td className="webhook-cell-mono">{event.user_id ?? '--'}</td>
                      <td className="webhook-cell-type">{event.event_type}</td>
                      <td className="webhook-cell-mono">{event.attempts}</td>
                      <td className="webhook-cell-error" title={event.last_error || ''}>
                        {event.last_error
                          ? (event.last_error.length > 60
                              ? event.last_error.slice(0, 60) + '…'
                              : event.last_error)
                          : '--'}
                      </td>
                      <td className="webhook-cell-actions">
                        <button
                          className="webhook-action-btn"
                          onClick={() => openDetail(event.id)}
                          title="View payload"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        {(event.status === 'failed' || event.status === 'done') && (
                          <button
                            className="webhook-action-btn"
                            onClick={() => handleRetry(event.id)}
                            disabled={retryingId === event.id}
                            title="Retry"
                          >
                            <i className={`bi bi-arrow-clockwise ${retryingId === event.id ? 'webhook-spin' : ''}`}></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderStatsTab = () => {
    if (loading) return renderLoading();
    if (!stats) return renderEmptyState();

    const byStatus = stats.byStatus || {};

    return (
      <div className="webhook-stats-section">
        <div className="webhook-stats-grid webhook-stats-grid-single">
          <div className="webhook-stat-card webhook-stat-card-highlight">
            <div className="webhook-stat-card-icon">
              <i className="bi bi-lightning-charge-fill"></i>
            </div>
            <div className="webhook-stat-card-body">
              <span className="webhook-stat-card-value">{total || 0}</span>
              <span className="webhook-stat-card-label">Total events in queue</span>
            </div>
          </div>
        </div>

        {Object.keys(byStatus).length > 0 && (
          <div className="webhook-stats-block">
            <h3 className="webhook-stats-block-title">
              <i className="bi bi-flag"></i>
              Events by Status
            </h3>
            <div className="webhook-stats-grid">
              {Object.entries(byStatus).map(([status, count]) => {
                const cfg = getStatusConfig(status);
                return (
                  <div key={status} className="webhook-stat-card">
                    <div className="webhook-stat-card-body">
                      <span className="webhook-stat-card-value">{count}</span>
                      <span className="webhook-stat-card-label">
                        <span className={`history-cell-status-badge ${cfg.className}`}>
                          <i className={`bi ${cfg.icon}`}></i>
                          {cfg.label}
                        </span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDetailModal = () => {
    if (!detailEvent) return null;
    return (
      <div className="webhook-modal-backdrop" onClick={closeDetail}>
        <div className="webhook-modal" onClick={(e) => e.stopPropagation()}>
          <div className="webhook-modal-header">
            <h3>Webhook event {detailEvent.id?.slice(0, 8)}…</h3>
            <button className="webhook-modal-close" onClick={closeDetail} aria-label="Close">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="webhook-modal-body">
            {detailLoading ? renderLoading() : (
              <>
                <dl className="webhook-detail-grid">
                  <dt>Status</dt><dd>{detailEvent.status}</dd>
                  <dt>Type</dt><dd>{detailEvent.event_type}</dd>
                  <dt>Event key</dt><dd className="webhook-cell-mono">{detailEvent.event_key}</dd>
                  <dt>Attempt ID</dt><dd>{detailEvent.attempt_id ?? '--'}</dd>
                  <dt>User ID</dt><dd>{detailEvent.user_id ?? '--'}</dd>
                  <dt>Attempts</dt><dd>{detailEvent.attempts}</dd>
                  <dt>Received</dt><dd>{formatTime(detailEvent.received_at)}</dd>
                  <dt>Processed</dt><dd>{formatTime(detailEvent.processed_at)}</dd>
                  <dt>Next retry</dt><dd>{formatTime(detailEvent.next_retry_at)}</dd>
                </dl>
                {detailEvent.last_error && (
                  <div className="webhook-detail-error">
                    <strong>Last error</strong>
                    <pre>{detailEvent.last_error}</pre>
                  </div>
                )}
                {detailEvent.payload && (
                  <div className="webhook-detail-payload">
                    <strong>Payload</strong>
                    <pre>{JSON.stringify(detailEvent.payload, null, 2)}</pre>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="webhooks-page">
      <div className="webhooks-page-header">
        <div>
          <h1 className="webhooks-page-title">Webhooks</h1>
          <p className="webhooks-page-subtitle">
            Real-time event queue from Testpress
          </p>
        </div>

        <div className="webhooks-header-actions">
          <button
            className="webhook-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh events"
          >
            <i className={`bi bi-arrow-clockwise ${refreshing ? 'webhook-spin' : ''}`}></i>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>

          <nav className="logs-tab-nav" aria-label="Webhooks sub-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`logs-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                <i className={`bi ${tab.icon}`}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {renderWebhookBanners()}

      {activeTab === 'events' && renderEventsTab()}
      {activeTab === 'stats' && renderStatsTab()}

      {renderDetailModal()}
    </div>
  );
};

export default WebhooksPage;
